/**
 * LicenseUtilization: the Licenses tab body on User Analytics, in Carbon.
 *
 *   1. Idle spend tile  the license-waste brief: `headlineMonthly` is the
 *                       tile figure (blueprint §6.2 row 9), then the brief as
 *                       a `BriefHero size="tile"` (action-first headline, one
 *                       lede, the pricing footnote as its provenance). The
 *                       page's PillarBrief owns the only h1, so the tile's
 *                       h2 title leads and the brief's headline is an h3.
 *   2. License mix      paid seats by SKU utilization as one segmented bar
 *                       (proportional, neutral grays; the key carries the
 *                       words), never a hue per tier.
 *   3. Where seats sit idle  the top five SKUs by monthly idle spend, as a
 *                       DataTable.
 *   4. All licenses     every SKU as a DataTable, filtered by contained tabs
 *                       (All / Paid / Free & trial / Idle).
 *
 * Color carries no state here (the Neutral Bad State Rule): utilization and
 * idle cost are plain tabular figures; the tier words live in the mix key.
 */

import { useMemo, useState } from 'react'
import type { LicenseSkuSummary, UserInsightsSummary } from '@/types'
import { useTheme } from '@/context/ThemeContext'
import { composeLicenseWasteBrief } from '@/lib/license-waste-brief'
import { isFreeOrTrialSku, isPaidSeatSku, isStorageAddOnSku } from '@/lib/license-skus'
import EmptyState from '@/components/shared/EmptyState'
import { BigNumber } from '@/components/carbon/BigNumber'
import { BriefHero } from '@/components/carbon/BriefHero'
import { Button } from '@/components/carbon/Button'
import { ContainedTabs } from '@/components/carbon/ContainedTabs'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { IconChevronDown } from '@/components/carbon/icons'
import { ScoreBar } from '@/components/carbon/ScoreBar'
import { SegmentedMeter, type MeterSegment } from '@/components/carbon/SegmentedMeter'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Tag } from '@/components/carbon/Tag'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'

interface Props {
  licenses: LicenseSkuSummary[]
  insights?: UserInsightsSummary | null
  isLoading: boolean
}

type UtilTier = 'healthy' | 'moderate' | 'under' | 'idle'
type FilterMode = 'all' | 'paid' | 'free' | 'idle'

export default function LicenseUtilization({ licenses, insights, isLoading }: Props) {
  useTheme()
  const [filter, setFilter] = useState<FilterMode>('all')
  const [tableExpanded, setTableExpanded] = useState(true)

  const sorted = useMemo(
    () => [...licenses].sort((a, b) => b.utilizationPercentage - a.utilizationPercentage),
    [licenses],
  )
  // Seat math (mix, leaderboard, idle seats) covers paid per-seat SKUs only.
  // Storage add-ons are paid but count gigabytes, so they list under All only.
  const paid = useMemo(() => sorted.filter(l => isPaidSeatSku(l.skuPartNumber)), [sorted])
  const free = useMemo(() => sorted.filter(l => isFreeOrTrialSku(l.skuPartNumber)), [sorted])
  const storageCount = useMemo(() => sorted.filter(l => isStorageAddOnSku(l.skuPartNumber)).length, [sorted])

  // Leaderboard: paid SKUs with non-trivial idle spend, top 5 by $/mo.
  const leaders = useMemo(
    () =>
      paid
        .filter(l => (l.estimatedMonthlyWasteUsd ?? 0) > 0)
        .sort((a, b) => (b.estimatedMonthlyWasteUsd ?? 0) - (a.estimatedMonthlyWasteUsd ?? 0))
        .slice(0, 5),
    [paid],
  )

  // Every paid SKU with idle spend: the "Idle" filter's rows and its count.
  const idlePaid = useMemo(() => paid.filter(l => (l.estimatedMonthlyWasteUsd ?? 0) > 0), [paid])

  // The brief's seat count comes from the same SKUs as the idle dollars. The
  // server's totalLicensesUnassigned also sums unpriced SKUs and storage
  // add-ons, so it can put thousands of seats in a headline its dollars don't
  // cover. It reads the summary's own licenseSkus, not the `licenses` prop: the
  // two queries refresh separately, and the count must match the summary's
  // dollars and top SKU.
  const idleSeats = useMemo(
    () =>
      (insights?.licenseSkus ?? [])
        .filter(l => isPaidSeatSku(l.skuPartNumber) && (l.estimatedMonthlyWasteUsd ?? 0) > 0)
        .reduce((sum, l) => sum + l.availableLicenses, 0),
    [insights?.licenseSkus],
  )

  // Paid seats per utilization tier, for the mix meter.
  const tierSeats = useMemo(() => seatsByTier(paid), [paid])

  const totalPaidSeats = useMemo(
    () => paid.reduce((sum, l) => sum + l.totalLicenses, 0),
    [paid],
  )

  const visibleRows = useMemo(() => {
    switch (filter) {
      case 'paid':
        return paid
      case 'free':
        return free
      case 'idle':
        return idlePaid
      case 'all':
      default:
        return sorted
    }
  }, [filter, paid, free, sorted, idlePaid])

  const brief = composeLicenseWasteBrief({
    totalMonthlyWasteUsd: insights?.totalEstimatedMonthlyWasteUsd ?? null,
    totalAnnualWasteUsd: insights?.totalEstimatedAnnualWasteUsd ?? null,
    topIdleSkus: insights?.topIdleSkus,
    totalIdleSeats: idleSeats,
    tenantId: insights?.tenantId,
  })

  if (isLoading) return <LoadingSkeleton />
  if (licenses.length === 0) {
    return (
      <Tile as="section" aria-label="Licenses" padding={24}>
        <EmptyState
          headline="Guardian has no license data for this tenant yet."
          body="It appears after the next scan."
        />
      </Tile>
    )
  }

  return (
    <div className="space-y-12">
      <IdleSpendTile brief={brief} />

      <section aria-labelledby="license-mix-heading" className="min-w-0">
        <SectionHead
          id="license-mix-heading"
          title="License mix"
          meta={`${paid.length} paid SKU${paid.length === 1 ? '' : 's'} · ${totalPaidSeats.toLocaleString()} seats`}
        />
        <Tile className="mt-4">
          <MixMeter tierSeats={tierSeats} skuCount={paid.length} freeCount={free.length} storageCount={storageCount} />
        </Tile>
      </section>

      {leaders.length > 0 && (
        <Leaderboard rows={leaders} totalMonthlyWaste={insights?.totalEstimatedMonthlyWasteUsd ?? 0} />
      )}

      <section aria-labelledby="license-all-heading" className="min-w-0">
        <SectionHead
          id="license-all-heading"
          title="All licenses"
          meta={`${sorted.length} total · ${paid.length} paid · ${free.length} free or trial${
            storageCount > 0 ? ` · ${storageCount} storage add-on${storageCount === 1 ? '' : 's'}` : ''
          }`}
          action={
            <Button
              kind="ghost"
              size="sm"
              onClick={() => setTableExpanded(!tableExpanded)}
              aria-expanded={tableExpanded}
              aria-controls="license-sku-table"
              icon={<IconChevronDown className={tableExpanded ? 'rotate-180' : undefined} />}
            >
              {tableExpanded ? 'Hide table' : `Show table (${visibleRows.length})`}
            </Button>
          }
        />
        {tableExpanded && (
          <div id="license-sku-table" className="mt-4">
            <SkuTable
              rows={visibleRows}
              tabs={
                <ContainedTabs
                  aria-label="Filter licenses"
                  value={filter}
                  onChange={id => setFilter(id as FilterMode)}
                  tabs={[
                    { id: 'all', label: 'All', count: sorted.length },
                    { id: 'paid', label: 'Paid', count: paid.length },
                    { id: 'free', label: 'Free and trial', count: free.length },
                    { id: 'idle', label: 'Idle', count: idlePaid.length },
                  ]}
                />
              }
            />
          </div>
        )}
      </section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Idle spend tile                                                            */
/* -------------------------------------------------------------------------- */

/** The license-waste brief as a tile: the monthly idle spend is the figure,
 *  then the brief (BriefHero reads its copy; only `headlineMonthly`, the
 *  figure, is read directly). */
function IdleSpendTile({ brief }: { brief: ReturnType<typeof composeLicenseWasteBrief> }) {
  return (
    <Tile as="section" aria-labelledby="license-spend-headline" padding={24} className="min-w-0">
      <TileHeader title="Idle license spend" level={2} />
      <BigNumber value={brief.headlineMonthly} size="figure-lg" className="mt-5" />
      <BriefHero
        size="tile"
        headingLevel="h3"
        headingId="license-spend-headline"
        brief={brief}
        provenance="Priced at Microsoft retail list. Volume, EA or partner discounts lower your actual spend."
        className="mt-6"
      />
    </Tile>
  )
}

/* -------------------------------------------------------------------------- */
/* SectionHead                                                                */
/* -------------------------------------------------------------------------- */

function SectionHead({
  id,
  title,
  meta,
  action,
}: {
  id: string
  title: string
  meta: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
      <div className="min-w-0">
        <h2 id={id} className="m-0 text-heading font-normal text-text-primary">
          {title}
        </h2>
        <p className="m-0 mt-1 text-helper tabular-nums text-text-helper">{meta}</p>
      </div>
      {action}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Mix meter                                                                  */
/* -------------------------------------------------------------------------- */

/** Tier order, worst first; each tier steps one neutral lighter. */
const TIER_SEGMENTS: ReadonlyArray<{ tier: UtilTier; tone: MeterSegment['tone']; label: string }> = [
  { tier: 'idle', tone: 'strong', label: 'seats in SKUs under 25% used' },
  { tier: 'under', tone: 'med', label: 'in SKUs 25–49% used' },
  { tier: 'moderate', tone: 'low', label: 'in SKUs 50–74% used' },
  { tier: 'healthy', tone: 'off', label: 'in SKUs 75% used or more' },
]

function MixMeter({
  tierSeats,
  skuCount,
  freeCount,
  storageCount,
}: {
  tierSeats: Record<UtilTier, number>
  skuCount: number
  freeCount: number
  storageCount: number
}) {
  return (
    <>
      <SegmentedMeter
        caption={`Paid seats across ${skuCount} SKU${skuCount === 1 ? '' : 's'}, by how much of each SKU is assigned`}
        segments={TIER_SEGMENTS.map(s => ({
          id: s.tier,
          value: tierSeats[s.tier],
          tone: s.tone,
          label: s.label,
        }))}
      />
      {(freeCount > 0 || storageCount > 0) && (
        <p className="m-0 mt-3 text-helper text-text-helper">
          {freeCount > 0 && storageCount > 0
            ? `Free and trial SKUs (${freeCount}) and storage add-ons (${storageCount}) are left out.`
            : freeCount > 0
              ? `Free and trial SKUs (${freeCount}) are left out.`
              : `Storage add-ons (${storageCount}) are left out.`}
        </p>
      )}
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* Leaderboard                                                                */
/* -------------------------------------------------------------------------- */

function Leaderboard({ rows, totalMonthlyWaste }: { rows: LicenseSkuSummary[]; totalMonthlyWaste: number }) {
  const leaderTotal = rows.reduce((sum, l) => sum + (l.estimatedMonthlyWasteUsd ?? 0), 0)

  const columns: DataColumn<LicenseSkuSummary>[] = [
    {
      id: 'license',
      header: 'License',
      primary: true,
      cell: sku => {
        const idleSeats = sku.availableLicenses
        return (
          <TableItem
            title={sku.displayName || sku.skuPartNumber}
            sub={
              <>
                {idleSeats.toLocaleString()} unused seat{idleSeats === 1 ? '' : 's'} ·{' '}
                {sku.usedLicenses.toLocaleString()} of {sku.totalLicenses.toLocaleString()} used
                {sku.monthlyPricePerLicense != null && (
                  <>
                    {' · '}${sku.monthlyPricePerLicense.toLocaleString('en-US', { maximumFractionDigits: 2 })}/seat/mo
                  </>
                )}
              </>
            }
          />
        )
      },
    },
    {
      id: 'utilization',
      header: 'Utilization',
      width: '220px',
      hideBelow: 'c-md',
      cell: sku => (
        <div className="flex items-center gap-3">
          <ScoreBar
            value={sku.utilizationPercentage}
            label={`${sku.displayName || sku.skuPartNumber} utilization`}
            className="min-w-[96px] flex-1"
          />
          <span className="w-10 flex-none text-right tabular-nums text-text-primary">{sku.utilizationPercentage}%</span>
        </div>
      ),
    },
    {
      id: 'idle',
      header: 'Idle / mo',
      align: 'end',
      nowrap: true,
      cell: sku => (
        <span className="font-semibold tabular-nums text-text-primary">
          ${Math.round(sku.estimatedMonthlyWasteUsd ?? 0).toLocaleString()}
        </span>
      ),
    },
  ]

  return (
    <DataTable
      title="Where seats sit idle"
      description="The five paid licenses with the most monthly idle spend."
      columns={columns}
      rows={rows}
      rowId={sku => sku.skuId}
      rowLabel={sku => sku.displayName || sku.skuPartNumber}
      footer={
        totalMonthlyWaste > 0 ? (
          <span>
            The {rows.length === 1 ? 'license' : `${rows.length} licenses`} above{' '}
            {rows.length === 1 ? 'accounts' : 'account'} for{' '}
            <b className="font-semibold text-text-primary">${Math.round(leaderTotal).toLocaleString()}/mo</b> of the
            tenant&rsquo;s{' '}
            <b className="font-semibold text-text-primary">${Math.round(totalMonthlyWaste).toLocaleString()}/mo</b> idle
            spend.
          </span>
        ) : undefined
      }
    />
  )
}

/* -------------------------------------------------------------------------- */
/* SkuTable                                                                   */
/* -------------------------------------------------------------------------- */

function SkuTable({ rows, tabs }: { rows: LicenseSkuSummary[]; tabs: React.ReactNode }) {
  const columns: DataColumn<LicenseSkuSummary>[] = [
    {
      id: 'license',
      header: 'License',
      primary: true,
      cell: license => {
        const isFree = isFreeOrTrialSku(license.skuPartNumber)
        const isStorage = isStorageAddOnSku(license.skuPartNumber)
        return (
          <span className="inline-flex flex-wrap items-center gap-2">
            <b className="font-semibold text-text-primary">{license.displayName || license.skuPartNumber}</b>
            {isFree && <Tag kind="outline">Free or trial</Tag>}
            {isStorage && <Tag kind="outline">Storage add-on</Tag>}
          </span>
        )
      },
    },
    {
      id: 'used',
      header: 'Used',
      align: 'end',
      nowrap: true,
      cell: license => (
        <>
          <span className="tabular-nums">{license.usedLicenses.toLocaleString()}</span>
          <PhoneWord> used</PhoneWord>
        </>
      ),
    },
    {
      id: 'total',
      header: 'Total',
      align: 'end',
      nowrap: true,
      hideBelow: 'c-lg',
      cell: license => <span className="tabular-nums">{license.totalLicenses.toLocaleString()}</span>,
    },
    {
      id: 'available',
      header: 'Available',
      align: 'end',
      nowrap: true,
      cell: license => (
        <>
          <span className="tabular-nums">{license.availableLicenses.toLocaleString()}</span>
          <PhoneWord> available</PhoneWord>
        </>
      ),
    },
    {
      id: 'utilization',
      header: 'Utilization',
      align: 'end',
      nowrap: true,
      cell: license => <span className="tabular-nums">{license.utilizationPercentage}%</span>,
    },
    {
      id: 'cost',
      header: 'Cost / mo',
      align: 'end',
      nowrap: true,
      hideBelow: 'c-lg',
      cell: license => {
        const monthlyCost =
          license.monthlyPricePerLicense != null
            ? license.totalLicenses * license.monthlyPricePerLicense
            : null
        return <span className="tabular-nums">{formatUsdWhole(monthlyCost)}</span>
      },
    },
    {
      id: 'idle',
      header: 'Idle / mo',
      align: 'end',
      nowrap: true,
      cell: license => {
        const idleWaste = license.estimatedMonthlyWasteUsd ?? null
        const isFree = isFreeOrTrialSku(license.skuPartNumber)
        return (
          <span className={isFree || idleWaste == null ? 'tabular-nums text-text-helper' : 'tabular-nums text-text-primary'}>
            {formatUsdMonthly(idleWaste)}
            {idleWaste != null ? <PhoneWord> idle</PhoneWord> : null}
          </span>
        )
      },
    },
  ]

  return (
    <DataTable
      aria-label="All licenses"
      tabs={tabs}
      columns={columns}
      rows={rows}
      rowId={license => license.skuId}
      rowLabel={license => license.displayName || license.skuPartNumber}
      empty="Nothing to review in this view."
      footer={
        <span className="text-helper text-text-helper">
          Cost figures use Microsoft retail list pricing. Volume, EA, or partner discounts will lower your actual spend.
        </span>
      }
    />
  )
}

/** A unit word for the stacked phone rows, where the column headers hide. */
function PhoneWord({ children }: { children: string }) {
  return <span className="c-md:hidden">{children}</span>
}

/* -------------------------------------------------------------------------- */
/* LoadingSkeleton                                                            */
/* -------------------------------------------------------------------------- */

function LoadingSkeleton() {
  return (
    <div className="space-y-12" aria-busy="true">
      <Tile padding={24}>
        <Skeleton lines={1} width="10rem" />
        <div aria-hidden="true" className="mt-5 h-14 w-64 max-w-full animate-pulse bg-layer-hover" />
        <Skeleton lines={2} className="mt-6" />
      </Tile>
      <Tile>
        <Skeleton lines={2} label="Loading license data" />
      </Tile>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function tierOf(percent: number): UtilTier {
  if (percent >= 75) return 'healthy'
  if (percent >= 50) return 'moderate'
  if (percent >= 25) return 'under'
  return 'idle'
}

function seatsByTier(skus: LicenseSkuSummary[]): Record<UtilTier, number> {
  const out: Record<UtilTier, number> = { healthy: 0, moderate: 0, under: 0, idle: 0 }
  for (const sku of skus) out[tierOf(sku.utilizationPercentage)] += sku.totalLicenses
  return out
}

function formatUsdMonthly(value: number | undefined | null): string {
  if (value == null) return '—'
  if (value === 0) return '$0/mo'
  const whole = value < 1 ? 1 : Math.round(value)
  return `$${whole.toLocaleString('en-US')}/mo`
}

function formatUsdWhole(value: number | undefined | null): string {
  if (value == null) return '—'
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}
